import { Platform } from '@angular/cdk/platform';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MtnaLogger } from '@mtna/core-angular';
import { RdsRegressionQuery } from '@rds/rds-base-ts';
import { DocumentOptionSet, OptionBundle, OptionSet, PackagingOptions } from '@rds/rds-packaging-ts';
import {
  RdsApiService,
  SelectParameters,
  serializeSelectOptionsUrlParams,
  serializeSelectQueryParams,
  serializeTabulationOptionsUrlParams,
  TabulationParameters,
} from '@rds/shared-api';
import { Observable, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RdsPackagingService {
  get PACKAGE_URL(): string {
    let apiUrl: string = this.apiService.getApiUrl() || '';
    if (!apiUrl?.endsWith('/app-api')) {
      // Support backwards compatibility
      // The API url should no longer end with '/api',
      // but point to the root of the rds instance.
      apiUrl += '/app-api';
    }
    return `${apiUrl}/package`;
  }

  get DATA_PRODUCT_URL(): string {
    return `${this.PACKAGE_URL}/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}`;
  }

  protected _bundlesCache$ = new ReplaySubject<OptionBundle[]>(1);
  protected _bundlesRequest$: Observable<OptionBundle[]> | undefined;
  protected _optionsCache$ = new ReplaySubject<PackagingOptions>(1);
  protected _optionsRequest$: Observable<PackagingOptions> | undefined;

  constructor(protected apiService: RdsApiService, private http: HttpClient, protected logger: MtnaLogger, protected platform: Platform) {}

  downloadPackagedFile(processId: string): void {
    const a = document.createElement('a');
    a.setAttribute('href', `${this.PACKAGE_URL}/${processId}`);
    a.setAttribute('target', '_blank');
    document.body.appendChild(a);
    this.logger.log('[@rds/components] RdsPackagingService#downloadPackagedFile - generated anchor:', a);
    a.click();

    if (this.platform.SAFARI) {
      this.logger.log('Safari detected, using window.location.href to download file.');
      window.location.href = a.href;
    }
    a.parentElement?.removeChild(a);
  }

  /**
   * Get bundle. When a client wants to get a particular bundle they can use this endpoint
   * to retrieve a list of OptionSets that are pre-configured to work with each other.
   * @param bundleId The ID of the bundle whose options are desired
   */
  getBundle(bundleId: string): Observable<OptionSet[]> {
    return this.http.get<OptionSet[]>(`${this.PACKAGE_URL}/option/bundle/${bundleId}`);
  }

  /**
   * Get all packaging bundles.
   * These are groups of related OptionSets grouped together with some pre-configuration already done.
   * This can ensure that users can get a set of options quickly and already set up with all the options needed.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns all packaging bundles
   */
  getBundles(refresh: boolean = false): Observable<OptionBundle[]> {
    if (refresh || !this._bundlesRequest$) {
      this.logger.debug(`[RdsPackagingService] getBundles - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._bundlesRequest$ = this.http.get<OptionBundle[]>(`${this.PACKAGE_URL}/option/bundles`);
      this._bundlesRequest$.subscribe(
        (result) => this._bundlesCache$.next(result),
        (err) => this._bundlesCache$.error(err)
      );
    }
    return this._bundlesCache$.asObservable();
  }

  getOption(formatId: string): Observable<OptionSet> {
    return this.http.get<OptionSet>(`${this.PACKAGE_URL}/options/${formatId}`);
  }

  /**
   * Get document packaging options.
   *
   * This provides a list of all the documents that are available to be included
   * in packaged output for the specified data product.
   *
   * These options can be used with any of the other methods of packaging (select,
   * tabulate, regression).
   */
  getDataProductDocuments(): Observable<DocumentOptionSet[]> {
    return this.http.get<DocumentOptionSet[]>(
      `${this.PACKAGE_URL}/option/documents/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}`
    );
  }

  /**
   * Get all packaging options.
   * This is a starting point for clients to identify all the possible packaging formats that are available.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns all packaging options.
   */
  getOptions(refresh: boolean = false): Observable<PackagingOptions> {
    if (refresh || !this._optionsRequest$) {
      this.logger.debug(`[RdsPackagingService] getOptions - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._optionsRequest$ = this.http.get<PackagingOptions>(`${this.PACKAGE_URL}/options`);
      this._optionsRequest$.subscribe(
        (result) => this._optionsCache$.next(result),
        (err) => this._optionsCache$.error(err)
      );
    }
    return this._optionsCache$.asObservable();
  }

  getClassificationOptions(): Observable<PackagingOptions> {
    return this.http.get<PackagingOptions>(`${this.PACKAGE_URL}/options/classification`);
  }

  getProcessDetails<Details>(processId: string): Observable<Details> {
    return this.http.get<Details>(`${this.PACKAGE_URL}/processdetails/${processId}`);
  }

  getPackagedFile(processId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.PACKAGE_URL}/${processId}`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  getPackageLimit(): Observable<number> {
    return this.http.get<number>(`${this.PACKAGE_URL}/limit`);
  }

  /**
   * Package the classification.
   * This packages the classification in preparation for it to be downloaded.
   * @param classificationId The ID for the classification.
   * @param optionSets The available packaging options for the classification.
   * @returns a processId
   */
  packageClassification(classificationId: string, optionSets: OptionSet[]): Observable<string> {
    return this.http.post<string>(`${this.DATA_PRODUCT_URL}/classification/${classificationId}`, optionSets);
  }

  /**
   * Packages the regression.
   * @param params The query parameters of the calculated regression
   * @param optionSets The selected option set(s) for packaging
   * @returns And ID of the process in progress
   */
  packageRegression(params: RdsRegressionQuery, optionSets: Array<OptionSet>): Observable<string> {
    const queryParameters = serializeSelectQueryParams({ ...params, inject: true });
    return this.http.post<string>(`${this.DATA_PRODUCT_URL}/regression?${queryParameters}`, optionSets);
  }

  select(params: SelectParameters, optionSets: OptionSet[]): Observable<string> {
    const paramsPath = serializeSelectOptionsUrlParams(params);
    return this.http.post<string>(`${this.DATA_PRODUCT_URL}/select?${paramsPath}`, optionSets);
  }

  /**
   * Package a tabulation.
   *
   * @param optionSets packaging format option sets
   * @param optionSets various tabulation parameters
   * @returns packaging ID
   */
  tabulate(params: TabulationParameters, optionSets: Array<OptionSet>): Observable<string> {
    const paramsPath = serializeTabulationOptionsUrlParams(params);
    return this.http.post<string>(`${this.DATA_PRODUCT_URL}/tabulate?${paramsPath}`, optionSets);
  }
}
